
import { useState, useCallback } from 'react';
import { logger } from "@/utils/logger";

interface ElementPosition {
  element: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Hook to track and analyze element positions for debugging
 */
export const useLayerPositions = (isActive: boolean) => {
  const [elementPositions, setElementPositions] = useState<ElementPosition[]>([]);

  // Analyze layer positions
  const analyzeLayerPositions = useCallback(() => {
    if (!isActive) return;

    const positions: ElementPosition[] = [];

    // Check document position
    const docRect = document.documentElement.getBoundingClientRect();
    positions.push({
      element: 'document',
      top: docRect.top,
      left: docRect.left,
      width: docRect.width,
      height: docRect.height
    });

    // Check body position
    const bodyRect = document.body.getBoundingClientRect();
    positions.push({
      element: 'body',
      top: bodyRect.top,
      left: bodyRect.left,
      width: bodyRect.width,
      height: bodyRect.height
    });

    // Check #root position
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const rootRect = rootElement.getBoundingClientRect();
      positions.push({
        element: '#root',
        top: rootRect.top,
        left: rootRect.left,
        width: rootRect.width,
        height: rootRect.height
      });
    }

    // Check app-shell position if it exists
    const appShell = document.querySelector('.app-shell');
    if (appShell) {
      const shellRect = appShell.getBoundingClientRect();
      positions.push({
        element: '.app-shell',
        top: shellRect.top,
        left: shellRect.left,
        width: shellRect.width,
        height: shellRect.height
      });
    }

    // Check .app-shell-content position if it exists
    const appContent = document.querySelector('.app-shell-content');
    if (appContent) {
      const contentRect = appContent.getBoundingClientRect();
      positions.push({
        element: '.app-shell-content',
        top: contentRect.top,
        left: contentRect.left,
        width: contentRect.width,
        height: contentRect.height
      });
    }

    // Check first button position
    const firstButton = document.querySelector('button');
    if (firstButton) {
      const buttonRect = firstButton.getBoundingClientRect();
      positions.push({
        element: 'first-button',
        top: buttonRect.top,
        left: buttonRect.left,
        width: buttonRect.width,
        height: buttonRect.height
      });
    }

    setElementPositions(positions);
    logger.info('Layer positions:', positions);
  }, [isActive]);

  return { elementPositions, analyzeLayerPositions };
};
